import { useQueryClient } from '@tanstack/react-query';
import {
  homeSectionProductsQueryOptions,
  useHomeSectionProductMutations,
} from '@/features/cms/mainPage/hooks/useHomeSectionsQuery';
import type { HomeSection, HomeSectionProduct } from '@/features/cms/mainPage/types/homeSection';

/** 섹션별 일괄 동작 — ADD = 노출(맨 앞에 넣기), REMOVE = 노출 해제. 없는 키는 변경 안 함. */
export type BulkHomeSectionChanges = Record<string, 'ADD' | 'REMOVE'>;

/**
 * QA-589 — 상품 일괄 변경의 [메인 섹션 노출]. 메인 페이지 관리·상품 폼(QA-570)과 같은 홈 섹션 API 로
 * 고른 상품들을 섹션에 넣거나 뺀다(BE 변경 없음). 넣을 때는 이미 있는 상품을 건너뛰고 새 상품을 맨 앞에 둔다.
 * 실패한 섹션 라벨을 돌려준다.
 */
export const useBulkHomeSections = () => {
  const qc = useQueryClient();
  const { add, reorder, remove } = useHomeSectionProductMutations();

  return async (
    sections: ReadonlyArray<Pick<HomeSection, 'id' | 'sectionKey' | 'label'>>,
    productIds: ReadonlyArray<string>,
    changes: BulkHomeSectionChanges,
  ): Promise<string[]> => {
    const failed: string[] = [];
    for (const section of sections) {
      const action = changes[section.sectionKey];
      if (!action) continue;
      try {
        const current: HomeSectionProduct[] = await qc.fetchQuery({ ...homeSectionProductsQueryOptions(section.id), staleTime: 0 });
        const has = new Set(current.map((p) => p.productId));
        if (action === 'ADD') {
          const toAdd = productIds.filter((id) => !has.has(id));
          if (toAdd.length === 0) continue;
          await add.mutateAsync({ sectionId: section.id, productIds: toAdd });
          const rest = [...current].sort((a, b) => a.sortOrder - b.sortOrder).map((p) => p.productId);
          await reorder.mutateAsync({ sectionId: section.id, productIds: [...toAdd, ...rest] });
        } else {
          for (const id of productIds.filter((pid) => has.has(pid))) {
            await remove.mutateAsync({ sectionId: section.id, productId: id });
          }
        }
      } catch {
        failed.push(section.label);
      }
    }
    return failed;
  };
};
